import { BehaviorSubject } from 'rxjs';
import { fabric } from 'fabric';
import { Injectable } from '@angular/core';
import { IEvent } from 'fabric/fabric-impl';
import { SessionSharedDataService } from '../../../services/session-shared-data.service';
import { SpaceRepository } from '../../../state/space.repository';

import { YCanvas, YStaticCanvas } from '../wb-canvas/fabric-utils';
import { isBoardItem, YObject } from '../../common/y-session';
import { ItemModel } from '../../../models/itemModel';
import { RealtimeSpaceService } from '../../../services/realtime-space.service';
import { FLAGS, FlagsService } from '../../../services/flags.service';
import { BoardItem } from './board-items/board.item';
import { FabricObject } from './board-items/board-items-manager';
import { BoardImage } from './board-items/board-image';
import { BoardPdf } from './board-items/board-pdf';

export enum StackingPosition {
  BACK = 'back',
  FRONT = 'front',
}
export type SortableFabricObject = (BoardItem | YObject) & {
  sortingCriteria?: number;
  order?: number;
};

@Injectable({
  providedIn: 'root',
})
export class BoardItemsStackingOrderManager {
  private readonly _maxZIndexOnTheBoard$ = new BehaviorSubject<number>(-1);
  private readonly _minZIndexOnTheBoard$ = new BehaviorSubject<number>(0);

  private boundOnFabricObjectAdded = this.onFabricObjectAdded.bind(this);

  constructor(
    private sharedDataService: SessionSharedDataService,
    private spaceRepo: SpaceRepository,
    private realtimeSpaceService: RealtimeSpaceService,
    private flagsService: FlagsService,
  ) {}

  public init() {
    this.sharedDataService.fabricCanvas?.on('object:added', this.boundOnFabricObjectAdded);
  }

  public destroyService() {
    this.sharedDataService.fabricCanvas?.off('object:added', this.boundOnFabricObjectAdded);
  }

  public clearState() {
    this._maxZIndexOnTheBoard$.next(-1);
    this._minZIndexOnTheBoard$.next(0);
  }

  public generateMaxZIndex() {
    this._maxZIndexOnTheBoard$.next(this.maxZIndexOnTheBoard + 1);
    return this.maxZIndexOnTheBoard;
  }

  private generateMinZIndex() {
    this._minZIndexOnTheBoard$.next(this.minZIndexOnTheBoard$ - 1);
    return this.minZIndexOnTheBoard$;
  }

  get maxZIndexOnTheBoard() {
    return this._maxZIndexOnTheBoard$.getValue();
  }
  get minZIndexOnTheBoard$() {
    return this._minZIndexOnTheBoard$.getValue();
  }

  private syncFabricObjectStackingWithServer(itemSelected: fabric.Object, bringToFront: boolean) {
    if (this.flagsService.isFlagEnabled(FLAGS.RENDER_IMAGES_IN_FABRIC)) {
      itemSelected['order'] = bringToFront ? this.generateMaxZIndex() : this.generateMinZIndex();

      this.sharedDataService.fabricCanvas?.fire('object:modified', { target: itemSelected });
    } else {
      // This is just for the sake of backward compatibility and should be remove once all users on the latest version
      this.realtimeSpaceService.service.changeObjectStacking(itemSelected['uid'], bringToFront);
    }
  }

  sendFabricObjectToBack(itemSelected: fabric.Object): void {
    if (!this.sharedDataService.fabricCanvas || !this.spaceRepo.activeSpaceId) {
      return;
    }
    // Maintaining that all fabric objects are above all other html items at all times
    const htmlItemsCount = this.sharedDataService.fabricCanvas
      .getObjects()
      .filter((item) => isBoardItem(item) && item.hasHtmlContent()).length;

    this.sharedDataService.fabricCanvas?.moveTo(itemSelected, htmlItemsCount);
    this.sharedDataService.fabricCanvas?.requestRenderAll();

    this.syncFabricObjectStackingWithServer(itemSelected, false);
  }

  bringFabricObjectToFront(itemSelected: fabric.Object): void {
    this.sharedDataService.fabricCanvas?.bringToFront(itemSelected);
    this.sharedDataService.fabricCanvas?.requestRenderAll();

    this.syncFabricObjectStackingWithServer(itemSelected, true);
  }

  bringBoardItemToFront(item: BoardItem) {
    this.changeIndex(item, StackingPosition.FRONT);
  }

  sendBoardItemToBack(item: BoardItem) {
    this.changeIndex(item, StackingPosition.BACK);
  }

  private getObjectsWithHtmlCount() {
    const objects = this.sharedDataService.fabricCanvas?.getObjects() ?? [];
    const objectsWithHtml = objects.filter(
      (object) => isBoardItem(object) && object.hasHtmlContent(),
    );
    return objectsWithHtml.length;
  }

  private changeIndex(item: BoardItem, newState: StackingPosition): void {
    if (!this.spaceRepo.activeSpace || !this.sharedDataService.fabricCanvas) {
      return;
    }

    const shouldBringToFront = newState === StackingPosition.FRONT;

    const objectsWithHtmlCount = this.getObjectsWithHtmlCount();
    const allObjectsCount = this.sharedDataService.fabricCanvas.getObjects().length;

    // an item with HTML content in the front should be only in front of other HTML element while still behind all other items with no HTML content
    const frontIndex = item.hasHtmlContent() ? objectsWithHtmlCount - 1 : allObjectsCount - 1;
    const backIndex = item.hasHtmlContent() ? 0 : objectsWithHtmlCount;

    item.moveTo(shouldBringToFront ? frontIndex : backIndex);
    this.sharedDataService.fabricCanvas.requestRenderAll();

    item.changeState({
      order: shouldBringToFront ? this.generateMaxZIndex() : this.generateMinZIndex(),
      // if the item was an HTML item and now a fabric item
      // we change stored stacking in the server,
      // to be handled differently on the first insertion of items
      migratedFromHtmlToFabric: this.isMigratedFromHtmlToFabric(item),
    });
  }

  onFabricObjectAdded(e: IEvent<Event>) {
    const target = e.target;
    if (isBoardItem(target) && target.hasHtmlContent()) {
      this.boardItemsWithHtmlAdded();
    } else if (target && !isBoardItem(target)) {
      target['order'] = this.generateMaxZIndex();
    }
  }

  public boardItemsWithHtmlAdded() {
    if (!this.sharedDataService.fabricCanvas) {
      return;
    }

    const fabricObjects = this.sharedDataService.fabricCanvas.getObjects();
    const boardItemsWithHtml = fabricObjects.filter(
      (fabricObject) => isBoardItem(fabricObject) && fabricObject.hasHtmlContent(),
    ) as BoardItem[];

    if (boardItemsWithHtml.length === 0) {
      this.clearState();
      return;
    }

    this.setMaxAndMinIndexOnBoard(boardItemsWithHtml);

    this.sendAddedBoardItemsWithHtmlToBack(boardItemsWithHtml);
  }

  setMaxAndMinIndexOnBoard(boardItemsWithHtml: BoardItem[]) {
    boardItemsWithHtml.forEach((boardItem, index) => {
      this._minZIndexOnTheBoard$.next(
        Math.min(this._minZIndexOnTheBoard$.getValue(), boardItem.configurations.order),
      );

      this._maxZIndexOnTheBoard$.next(
        Math.max(this._maxZIndexOnTheBoard$.getValue(), boardItem.configurations.order),
      );
    });
  }

  public getFabricObjectsWithInsertionOrder(
    canvasItems: BoardItem[],
    fabricObjects: SortableFabricObject[],
  ) {
    if (this.flagsService.isFlagEnabled(FLAGS.RENDER_IMAGES_IN_FABRIC)) {
      return this.newGetFabricObjectsWithInsertionOrder(canvasItems, fabricObjects);
    } else {
      return this.oldGetFabricObjectsWithInsertionOrder(canvasItems, fabricObjects);
    }
  }

  // in fabric, insertion order reflects the stacking order, and board items (with HTML) should be always added before plain fabric objects
  // because the whole container that holds the related HTML content of a board item is behind the fabric canvas,
  // so the board item that has HTML should also be behind all other board items that have no HTML content, to match the visibility with the interactions expectations for the user
  // .i.e avoiding the case where an image appear below a shape but when clicking the shape the image will be the one selected
  public oldGetFabricObjectsWithInsertionOrder(
    boardItems: BoardItem[],
    fabricObjects: fabric.Object[],
  ) {
    boardItems.sort((a, b) => a.configurations.order - b.configurations.order);

    return boardItems.concat(fabricObjects as any[]);
  }

  public newGetFabricObjectsWithInsertionOrder(
    canvasItems: BoardItem[],
    fabricObjects: SortableFabricObject[],
  ) {
    // canvas items should be inserted according to the order stored in them
    canvasItems.sort((a, b) => a.configurations.order - b.configurations.order);

    // We need to differentiate between the migrated canvas items and the non-migrated ones
    // Non migrated are inserted normally and ordered between themselves
    const nonMigratedHtmlItems = canvasItems.filter(
      (boardItem) => !this.isMigratedFromHtmlToFabric(boardItem),
    );

    // For migratedHtmlItemsToFabric, we need to differentiate wither the items stacking was manipulated after it was migrated or not
    const migratedHtmlItemsToFabric = canvasItems.filter((boardItem) =>
      this.isMigratedFromHtmlToFabric(boardItem),
    );

    // for the ones not manipulated, we keep the relative order between them and sort them with other fabric objects
    fabricObjects = fabricObjects.concat(migratedHtmlItemsToFabric);

    fabricObjects.forEach((o, i) => {
      // storing the order so that they will get sorted according to it easily
      if (isBoardItem(o)) {
        o.sortingCriteria = o.configurations.order;
      } else {
        // Either the order was manipulated before and order is stored or not and the index indicates the correct order
        o.sortingCriteria = o.order ?? i;
      }
    });

    fabricObjects.sort((a, b) => {
      // This array now contains plain fabric objects and fabric objects that are migrated from HTML
      // The ones migrated but their stacking is untouched, should be before all other fabric objects that their stacking is also untouched,
      // Because when they used to be HTML that used to be their stacking.
      if (
        this.isMigratedHtmlToFabricWithOriginalStackingOrder(a) ||
        this.isMigratedHtmlToFabricWithOriginalStackingOrder(b)
      ) {
        if (this.isPlainFabricObject(a) && a.order === undefined) {
          // a is plain fabric object with a stacking that did not change before the migration
          // and b is a non-touched migrated object, hence a should be after b
          return 1;
        } else if (this.isPlainFabricObject(b) && b.order === undefined) {
          // b is plain fabric object with a stacking that did not change before the migration
          // and a is a non-touched migrated object, hence b should be after a
          return -1;
        } else if (!this.isPlainFabricObject(a) && !this.isPlainFabricObject(b)) {
          // both a and b are migrated objects, hence they are sorted according to their order with no other constraint
          return (a as BoardImage).configurations.order - (b as BoardImage).configurations.order;
        }
      }
      return a.sortingCriteria! - b.sortingCriteria!;
    });

    // Maintaining the min and max z-indexes on the board to use them once changing the stacking of an item
    this.initMinAndMaxZIndexesObservables([
      ...nonMigratedHtmlItems.map((o) => o.configurations.order),
      ...fabricObjects.map((o: any) => o.sortingCriteria),
    ]);

    // No need to keep sortingCriteria as the needed info is stored either in the order variable or by inferring them from the items position in the array
    fabricObjects.forEach((o: any) => delete o.sortingCriteria);

    // at the end this is how the array should be ordered (but flattened)
    // [
    //    [html items],
    //    [
    //      html items that are now a fabric objects but no change to its stacking happened after the migration and
    //        (by default they are at the start of the array unless another object was sent to the back of this array)
    //      fabric objects that includes migrated items that the stacking order of them has been changed after the migration
    //    ]
    // ] // each subarray is sorted according to its stacking context
    return nonMigratedHtmlItems.concat(fabricObjects as any[]);
  }

  initMinAndMaxZIndexesObservables(array: number[]) {
    this._maxZIndexOnTheBoard$.next(Math.max(...array, this._maxZIndexOnTheBoard$.getValue()));
    this._minZIndexOnTheBoard$.next(Math.min(...array, this._minZIndexOnTheBoard$.getValue()));
  }

  private isPlainFabricObject(o) {
    return !isBoardItem(o) || o.itemType === ItemModel.StickyNote;
  }

  private isMigratedHtmlToFabricWithOriginalStackingOrder(o: any): o is BoardImage | BoardPdf {
    return this.isMigratedFromHtmlToFabric(o) && !o.configurations.migratedFromHtmlToFabric;
  }

  isMigratedFromHtmlToFabric(boardItem: BoardItem): boardItem is BoardPdf | BoardImage {
    return (
      (this.flagsService.isFlagEnabled(FLAGS.RENDER_PDFS_IN_FABRIC) &&
        boardItem.itemType === ItemModel.PDFImages) ||
      (this.flagsService.isFlagEnabled(FLAGS.RENDER_PDFS_IN_FABRIC) &&
        boardItem.itemType === ItemModel.PDF) ||
      (this.flagsService.isFlagEnabled(FLAGS.RENDER_IMAGES_IN_FABRIC) &&
        boardItem.itemType === ItemModel.Image)
    );
  }

  // This puts all newly added fabric objects with HTML below all other ones,
  //  the difference with sortFabricObjects is that it only iterates over fabric objects that has HTML content,
  //  which-in the average case-should be fewer than the ones that don't
  //  caller of this function should call it when only few fabric objects with HTML are added, to avoid performance issues cause by recurrent calls to the moveTo method
  private sendAddedBoardItemsWithHtmlToBack(boardItemsWithHtml: BoardItem[]) {
    if (!boardItemsWithHtml.length || !this.sharedDataService.fabricCanvas) {
      return;
    }

    const fabricObjects = this.sharedDataService.fabricCanvas.getObjects();
    if (fabricObjects.length < boardItemsWithHtml.length) {
      // make sure they are rendered
      return;
    }

    boardItemsWithHtml
      .sort((a, b) => a.configurations.order - b.configurations.order)
      .forEach((boardItemWithHtml, index) => {
        if (!this.sharedDataService.fabricCanvas) {
          return;
        }

        // if we should move the item one place to the back or one place to the front,
        //  we use swapInPlace method instead of moveTo, because it is more optimized
        if (index !== 0 && boardItemWithHtml === fabricObjects[index - 1]) {
          this.swapInPlace(fabricObjects, index, index - 1);
          this.sharedDataService.fabricCanvas.requestRenderAll();
          return;
        }

        if (index !== fabricObjects.length - 1 && boardItemWithHtml === fabricObjects[index + 1]) {
          this.swapInPlace(fabricObjects, index, index + 1);
          this.sharedDataService.fabricCanvas.requestRenderAll();
          return;
        }

        if (boardItemWithHtml === fabricObjects[index]) {
          return;
        }

        boardItemWithHtml.moveTo(index);
      });
  }

  public stackObjects(
    remoteObjects: FabricObject[],
    objectsModified: FabricObject[],
    canvas: YStaticCanvas | YCanvas,
  ) {
    if (this.flagsService.isFlagEnabled(FLAGS.RENDER_IMAGES_IN_FABRIC)) {
      this.newStackObjects(remoteObjects, objectsModified, canvas);
    } else {
      this.oldStackObjects(remoteObjects, canvas);
    }
  }

  private newStackObjects(
    remoteObjects: FabricObject[],
    objectsModified: FabricObject[],
    canvas: YStaticCanvas | YCanvas,
  ): void {
    if (!objectsModified) {
      return;
    }
    let localObjects = canvas.getObjects() as FabricObject[];

    const uidToLocalObjectMap = new Map<string, FabricObject>();
    localObjects.forEach((object) => {
      if (object.uid) {
        uidToLocalObjectMap.set(object.uid, object);
      }
    });

    let localObjectsIndex = 0;
    remoteObjects.forEach((object) => {
      // Skipping the elements excluded from export as they only exist on the local canvas,
      // and we don't need to align their stacking as this is the responsibility of the parent objects
      while (
        localObjectsIndex < localObjects.length &&
        localObjects[localObjectsIndex].excludeFromExport
      ) {
        localObjectsIndex++;
      }

      if (localObjectsIndex === localObjects.length) {
        return;
      }

      if (localObjects[localObjectsIndex].uid !== object.uid && object.uid) {
        const localObjectThatShouldBeAtCurrentIndex = uidToLocalObjectMap.get(object.uid);
        if (localObjectThatShouldBeAtCurrentIndex) {
          canvas.moveTo(localObjectThatShouldBeAtCurrentIndex, localObjectsIndex);

          // We need it to always reflect the state of the fabric objects array after each `moveTo` operation
          localObjects = canvas.getObjects() as FabricObject[];
        }
      }

      localObjectsIndex++;
    });
  }

  // This is the old method we sync stacking between users, we are keeping it for backward compatibility and should remove once all users are on the latest version
  private oldStackObjects(remoteObjects: any[], canvas: YStaticCanvas | YCanvas): void {
    const localObjects: any[] = canvas.getObjects();
    const moveToBackRemoteObject = remoteObjects[0];
    const bringToFrontRemoteObject = remoteObjects[remoteObjects.length - 1];

    if (!moveToBackRemoteObject && !bringToFrontRemoteObject) {
      return;
    }

    // The canvas objects array should always be like this: [ ...html elements, ...fabric object]
    // We are getting the index of the last html element to keep html elements before it and fabric objects after it
    const BoardItemsEndIndex = localObjects.filter(
      (obj) => isBoardItem(obj) && obj.hasHtmlContent(),
    ).length;

    for (const object of localObjects) {
      if (
        !object ||
        // objects excluded from export should be the responsibility of its parent object
        object.excludeFromExport
      ) {
        continue;
      }
      const isHtmlObject = isBoardItem(object) && object.hasHtmlContent();
      if (object.uid === moveToBackRemoteObject.uid) {
        isBoardItem(object) ? object.sendToBack() : canvas.moveTo(object, BoardItemsEndIndex);
      }
      if (object.uid === bringToFrontRemoteObject.uid) {
        isHtmlObject ? canvas.moveTo(object, BoardItemsEndIndex) : object.bringToFront();
      }
    }
  }

  private swapInPlace(array: any[], i: number, j: number) {
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }

  // We are handling the stacking order of copied objects both in the new stacking method (that relies on the order variable)
  // and the old one (that relies on the order in the array)
  public setObjectsNewStackingOrder(copiedObjects: FabricObject[]) {
    copiedObjects.forEach((o) => {
      const maxZIndex = this.generateMaxZIndex();
      if (isBoardItem(o)) {
        o.configurations.order = maxZIndex;
      } else {
        o['order'] = maxZIndex;
      }
    });
  }
}
